import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../utils/apiClient';
import AdminAuditLog from '../components/AdminAuditLog';

interface AdminUser {
  user_id: string;
  username: string;
  email: string;
  name: string;
  created_at: string;
  updated_at: string;
  is_current_user: boolean;
}

interface AdminRolesResponse {
  admins: AdminUser[];
  total_count: number;
}

const AdminRolesPage: React.FC = () => {
  const { userRole } = useAuth();
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRelinquishModal, setShowRelinquishModal] = useState(false);
  const [isRelinquishing, setIsRelinquishing] = useState(false);

  // Direct promotion state
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [promoteEmail, setPromoteEmail] = useState('');
  const [promoteMessage, setPromoteMessage] = useState('');
  const [isPromoting, setIsPromoting] = useState(false);
  const [promoteSuccess, setPromoteSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (userRole === 'admin') {
      loadAdmins();
    }
  }, [userRole]);

  const loadAdmins = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.get('/v1/admin/roles');
      const data: AdminRolesResponse = response.data.data;

      setAdmins(data.admins);
    } catch (err: any) {
      console.error('Failed to load admin users:', err);
      setError(err.response?.data?.error?.message || 'Failed to load admin users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRelinquishRole = async () => {
    try {
      setIsRelinquishing(true);

      await apiClient.delete('/v1/admin/role');

      // Reload the page to reflect the role change
      window.location.reload();
    } catch (err: any) {
      console.error('Failed to relinquish admin role:', err);
      setError(err.response?.data?.error?.message || 'Failed to relinquish admin role');
    } finally {
      setIsRelinquishing(false);
      setShowRelinquishModal(false);
    }
  };

  const handleDirectPromotion = async () => {
    try {
      setIsPromoting(true);
      setError(null);
      setPromoteSuccess(null);

      // Validate email
      if (!promoteEmail || !promoteEmail.trim()) {
        setError('Please enter an email address');
        return;
      }

      // Call the direct promotion API
      const response = await apiClient.post('/v1/admin/promote', {
        email: promoteEmail.trim(),
        message: promoteMessage.trim() || undefined,
      });

      const data = response.data.data;

      // Show success message
      setPromoteSuccess(`Successfully promoted ${data.email} to admin`);

      // Clear form
      setPromoteEmail('');
      setPromoteMessage('');

      // Reload admins list
      await loadAdmins();

      // Close modal after a short delay
      setTimeout(() => {
        setShowPromoteModal(false);
        setPromoteSuccess(null);
      }, 2000);

    } catch (err: any) {
      console.error('Failed to promote user:', err);
      setError(err.response?.data?.error?.message || 'Failed to promote user to admin');
    } finally {
      setIsPromoting(false);
    }
  };

  if (userRole !== 'admin') {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Access denied. Admin privileges required.
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1>
                <i className="fas fa-crown me-2 text-warning"></i>
                Admin Roles
              </h1>
              <p className="text-muted mb-0">
                Manage administrator roles and permissions
              </p>
            </div>
            <div>
              <button
                className="btn btn-primary me-2"
                onClick={() => setShowPromoteModal(true)}
              >
                <i className="fas fa-user-plus me-2"></i>
                Promote User to Admin
              </button>
              <button
                className="btn btn-outline-danger"
                onClick={() => setShowRelinquishModal(true)}
                disabled={admins.length <= 1}
                title={admins.length <= 1 ? "Cannot relinquish role - at least one admin must remain" : "Relinquish your admin role"}
              >
                <i className="fas fa-user-minus me-2"></i>
                Relinquish Admin Role
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-danger">
              <i className="fas fa-exclamation-triangle me-2"></i>
              <strong>Error:</strong> {error}
            </div>
          </div>
        </div>
      )}

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                Current Administrators
                <span className="badge bg-primary ms-2">{admins.length}</span>
              </h5>
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={loadAdmins}
                disabled={isLoading}
              >
                <i className="fas fa-sync-alt me-1"></i>
                Refresh
              </button>
            </div>
            <div className="card-body">
              {isLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2 text-muted">Loading administrators...</p>
                </div>
              ) : admins.length === 0 ? (
                <div className="text-center py-4">
                  <i className="fas fa-crown fa-3x text-muted mb-3"></i>
                  <h5 className="text-muted">No administrators found</h5>
                  <p className="text-muted">This shouldn't happen - there should always be at least one admin.</p>
                </div>
              ) : (
                <div className="row">
                  {admins.map((admin) => (
                    <div key={admin.user_id} className="col-lg-6 mb-3">
                      <div className={`card h-100 ${admin.is_current_user ? 'border-primary' : ''}`}>
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <h6 className="card-title mb-1">
                                <i className="fas fa-crown me-2 text-warning"></i>
                                {admin.name || 'No name provided'}
                                {admin.is_current_user && (
                                  <span className="badge bg-info ms-2">You</span>
                                )}
                              </h6>
                              <p className="text-muted small mb-1">
                                <i className="fas fa-envelope me-1"></i>
                                {admin.email}
                              </p>
                              {admin.username && admin.username !== admin.email && (
                                <p className="text-muted small mb-0">
                                  <i className="fas fa-at me-1"></i>
                                  {admin.username}
                                </p>
                              )}
                            </div>
                            <span className="badge bg-danger">
                              <i className="fas fa-shield-alt me-1"></i>
                              ADMIN
                            </span>
                          </div>

                          <div className="mt-3">
                            <div className="row text-center">
                              <div className="col-6">
                                <small className="text-muted d-block">Admin Since</small>
                                <strong className="small">
                                  {admin.created_at ? new Date(admin.created_at).toLocaleDateString() : 'Unknown'}
                                </strong>
                              </div>
                              <div className="col-6">
                                <small className="text-muted d-block">Last Updated</small>
                                <strong className="small">
                                  {admin.updated_at ? new Date(admin.updated_at).toLocaleDateString() : 'Unknown'}
                                </strong>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Admin Role Statistics */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card bg-light">
            <div className="card-body">
              <h6 className="card-title">
                <i className="fas fa-chart-bar me-2"></i>
                Admin Role Statistics
              </h6>
              <div className="row text-center">
                <div className="col-md-4">
                  <div className="border-end">
                    <h4 className="text-primary mb-1">{admins.length}</h4>
                    <small className="text-muted">Total Admins</small>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="border-end">
                    <h4 className="text-success mb-1">
                      {admins.filter(a => a.is_current_user).length}
                    </h4>
                    <small className="text-muted">Current User</small>
                  </div>
                </div>
                <div className="col-md-4">
                  <h4 className="text-info mb-1">
                    {admins.length > 1 ? 'Yes' : 'No'}
                  </h4>
                  <small className="text-muted">Can Relinquish Role</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Admin Actions */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h6 className="mb-0">
                <i className="fas fa-history me-2"></i>
                Recent Admin Actions
              </h6>
            </div>
            <div className="card-body">
              <AdminAuditLog maxEntries={10} showTitle={false} />
            </div>
          </div>
        </div>
      </div>

      {/* Direct Promotion Modal */}
      {showPromoteModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-user-plus text-primary me-2"></i>
                  Promote User to Admin
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowPromoteModal(false);
                    setPromoteEmail('');
                    setPromoteMessage('');
                    setError(null);
                    setPromoteSuccess(null);
                  }}
                  disabled={isPromoting}
                ></button>
              </div>
              <div className="modal-body">
                {promoteSuccess && (
                  <div className="alert alert-success">
                    <i className="fas fa-check-circle me-2"></i>
                    {promoteSuccess}
                  </div>
                )}

                {error && !promoteSuccess && (
                  <div className="alert alert-danger">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {error}
                  </div>
                )}

                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>Direct Promotion:</strong> This will immediately grant admin privileges to the user without requiring them to accept an invitation.
                </div>

                <div className="mb-3">
                  <label htmlFor="promoteEmail" className="form-label">
                    User Email <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    id="promoteEmail"
                    placeholder="user@example.com"
                    value={promoteEmail}
                    onChange={(e) => setPromoteEmail(e.target.value)}
                    disabled={isPromoting || !!promoteSuccess}
                    required
                  />
                  <small className="text-muted">
                    Enter the email address of the user you want to promote to admin
                  </small>
                </div>

                <div className="mb-3">
                  <label htmlFor="promoteMessage" className="form-label">
                    Message (Optional)
                  </label>
                  <textarea
                    className="form-control"
                    id="promoteMessage"
                    rows={3}
                    placeholder="Add a personal message to include in the notification email..."
                    value={promoteMessage}
                    onChange={(e) => setPromoteMessage(e.target.value)}
                    disabled={isPromoting || !!promoteSuccess}
                    maxLength={500}
                  />
                  <small className="text-muted">
                    {promoteMessage.length}/500 characters
                  </small>
                </div>

                <div className="alert alert-warning">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  <strong>Note:</strong> The user will receive an email notification about their new admin status and will have immediate access to all admin features.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowPromoteModal(false);
                    setPromoteEmail('');
                    setPromoteMessage('');
                    setError(null);
                    setPromoteSuccess(null);
                  }}
                  disabled={isPromoting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleDirectPromotion}
                  disabled={isPromoting || !promoteEmail.trim() || !!promoteSuccess}
                >
                  {isPromoting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Promoting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-user-plus me-2"></i>
                      Promote to Admin
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Relinquish Role Modal */}
      {showRelinquishModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-exclamation-triangle text-warning me-2"></i>
                  Relinquish Admin Role
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowRelinquishModal(false)}
                  disabled={isRelinquishing}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  <strong>Warning:</strong> This action cannot be undone without another admin's help.
                </div>

                <p>
                  <strong>Are you sure you want to relinquish your admin role?</strong>
                </p>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <h6 className="text-muted">Current Admin Status</h6>
                    <ul className="list-unstyled">
                      <li><i className="fas fa-check text-success me-2"></i>Full system access</li>
                      <li><i className="fas fa-check text-success me-2"></i>User management</li>
                      <li><i className="fas fa-check text-success me-2"></i>System configuration</li>
                      <li><i className="fas fa-check text-success me-2"></i>Admin role management</li>
                    </ul>
                  </div>
                  <div className="col-md-6">
                    <h6 className="text-muted">After Relinquishing</h6>
                    <ul className="list-unstyled">
                      <li><i className="fas fa-times text-danger me-2"></i>No admin access</li>
                      <li><i className="fas fa-times text-danger me-2"></i>Cannot manage users</li>
                      <li><i className="fas fa-times text-danger me-2"></i>Cannot change settings</li>
                      <li><i className="fas fa-times text-danger me-2"></i>Cannot invite admins</li>
                    </ul>
                  </div>
                </div>

                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>Remaining Admins:</strong> {admins.length - 1} admin(s) will remain in the system.
                  {admins.length === 2 && (
                    <div className="mt-2">
                      <small className="text-warning">
                        <i className="fas fa-exclamation-triangle me-1"></i>
                        Only one admin will remain. Consider having multiple admins for redundancy.
                      </small>
                    </div>
                  )}
                </div>

                <div className="form-check mt-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="confirmRelinquish"
                    onChange={(e) => {
                      const button = document.getElementById('relinquishButton') as HTMLButtonElement;
                      if (button) button.disabled = !e.target.checked;
                    }}
                  />
                  <label className="form-check-label" htmlFor="confirmRelinquish">
                    I understand that I will lose all admin privileges and will need to be re-invited by another admin.
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowRelinquishModal(false)}
                  disabled={isRelinquishing}
                >
                  Cancel
                </button>
                <button
                  id="relinquishButton"
                  type="button"
                  className="btn btn-danger"
                  onClick={handleRelinquishRole}
                  disabled={true}
                >
                  {isRelinquishing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Relinquishing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-user-minus me-2"></i>
                      Relinquish Admin Role
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRolesPage;
